import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
} from "antd";
import FormItemLabel from "antd/lib/form/FormItemLabel";
import zipCodes from "zipcodes";
import Flex from "../../../components/Flex";
import {
  getCustomerInfo,
  getCustomerAddresses,
} from "../../../store/actions/customerActionCreators";
import { CustomerAddressDto } from "../../../store/actions/dtos/CustomerAddressDto";
import { StateCode } from "./hooks/useOrderDetailsEdit";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------
export interface ShipToAddressValues {
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
}

export interface ShipToAddressModalProps {
  open: boolean;
  onClose: () => void;
  /** Called only when the user clicks OK and validation passes. The modal does
   *  NOT call the API — it just hands values back to the caller so they can be
   *  staged into the shared draft.  The actual PATCH fires when the user clicks
   *  the main Save button in QuoteVersionDetail. */
  onSave: (values: ShipToAddressValues) => void;
  initialValues: ShipToAddressValues;
  poolOwner: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  usStateOptions: StateCode[];
}

// ---------------------------------------------------------------------------
// Radio option enum — mirrors AutofillRadio from ShippingInformationPage
// ---------------------------------------------------------------------------

enum AutofillRadio {
  poolOwner = "poolOwner",
  billTo = "billTo",
  manual = "manual",
  shipTo = "shipTo",
}

// ---------------------------------------------------------------------------
// AutofilledValue — mirrors the wizard's AutofilledValue shape using local
// field names (shippingState instead of shippingStateCode)
// ---------------------------------------------------------------------------

type AutofilledValue = {
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
};

type SelectOption = {
  label: string;
  value: string;
};

// ---------------------------------------------------------------------------
// Internal form field names — mirrors ShipToAddressValues exactly
// ---------------------------------------------------------------------------

type ShipToForm = ShipToAddressValues;

// ---------------------------------------------------------------------------
// ShipToAutofillRadio — mirrors the wizard's ShipToAutofillRadio function
// ---------------------------------------------------------------------------

function ShipToAutofillRadio({
  setRadioValue,
  setCurrentlyAutofilledValue,
  autofillShipToInfo,
  setSelectedAutofillItem,
  customerAddresses,
  selectedAutofillItem,
  addressesLoading,
  selectedShipToItemState: [selectedShipToItem, setSelectedShipToItem],
}: {
  setRadioValue: (value: AutofillRadio) => void;
  setCurrentlyAutofilledValue: (value: AutofilledValue) => void;
  autofillShipToInfo: (value: AutofilledValue) => void;
  customerAddresses: CustomerAddressDto[] | null;
  selectedAutofillItem?: string;
  setShipToInfo: (value: AutofilledValue) => void;
  setSelectedAutofillItem: (value: AutofillRadio) => void;
  addressesLoading: boolean;
  selectedShipToItemState: [
    SelectOption | null,
    React.Dispatch<React.SetStateAction<SelectOption | null>>
  ];
}) {
  return (
    <Flex align="center">
      <Radio
        disabled={addressesLoading || !customerAddresses?.length}
        value={AutofillRadio.shipTo}
        onClick={() => {
          let selectedFullAddress: CustomerAddressDto | undefined;
          if (!!selectedShipToItem && !!customerAddresses?.length) {
            selectedFullAddress = customerAddresses.find(
              (address) =>
                address.address1.toLowerCase().trim() ===
                selectedShipToItem.value.toLowerCase().trim()
            );
          }
          if (!!selectedFullAddress) {
            const newValue = {
              shippingName: selectedFullAddress.company,
              shippingAddress: selectedFullAddress.address1,
              shippingCity: selectedFullAddress.city,
              shippingZip: selectedFullAddress.zip,
              shippingState: selectedFullAddress.state,
            };
            autofillShipToInfo(newValue);
            setCurrentlyAutofilledValue(newValue);
            setRadioValue(AutofillRadio.shipTo);
          }
        }}
      ></Radio>
      <Select
        placeholder="Ship to address"
        dropdownMatchSelectWidth={false}
        style={{ width: "150px" }}
        value={selectedShipToItem}
        disabled={addressesLoading || !customerAddresses?.length}
        loading={addressesLoading}
        labelInValue
        options={
          customerAddresses?.map((address) => ({
            label: address.company,
            value: address.address1,
          })) ?? []
        }
        onChange={(value) => {
          setSelectedAutofillItem(AutofillRadio.shipTo);
          const selectedAddress = customerAddresses?.find(
            (address) => address.address1 === value.value
          );
          if (!!selectedAddress) {
            const newValue = {
              shippingName: selectedAddress.company,
              shippingAddress: selectedAddress.address1,
              shippingCity: selectedAddress.city,
              shippingZip: selectedAddress.zip,
              shippingState: selectedAddress.state,
            };
            setCurrentlyAutofilledValue(newValue);
            autofillShipToInfo(newValue);
            setSelectedShipToItem(value);
          }
        }}
        onBlur={() => {
          if (!selectedAutofillItem) {
            setRadioValue(AutofillRadio.manual);
          }
        }}
      />
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ShipToAddressModal: React.FC<ShipToAddressModalProps> = ({
  open,
  onClose,
  onSave,
  initialValues,
  poolOwner,
  usStateOptions,
}) => {
  const [form] = Form.useForm<ShipToForm>();
  const [selectedRadio, setSelectedRadio] = useState<AutofillRadio>(
    AutofillRadio.manual
  );
  const [currentlyAutofilledValue, setCurrentlyAutofilledValue] =
    useState<AutofilledValue | null>(null);
  const [shipToAddresses, setShipToAddresses] =
    useState<CustomerAddressDto[] | null>(null);
  const [selectedShipToItem, setSelectedShipToItem] =
    useState<SelectOption | null>(null);
  const [addressesLoading, setAddressesLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // ── Load customer addresses when modal opens ─────────────────────────────
  // usStateOptions is fetched once by the parent (useOrderDetailsEdit) and passed in.

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
      setSelectedRadio(AutofillRadio.manual);
      setCurrentlyAutofilledValue(null);
      setSelectedShipToItem(null);

      setAddressesLoading(true);
      getCustomerAddresses().then(([addrResponse]) => {
        setAddressesLoading(false);
        if (addrResponse) setShipToAddresses(addrResponse.data ?? null);
      });
    }
  }, [open]); // intentionally only re-run when open changes

  // ── Form.useWatch — mirrors the wizard's watched values ─────────────────

  const name =
    Form.useWatch<string>("shippingName", { preserve: true, form }) ?? null;
  const address =
    Form.useWatch<string>("shippingAddress", { preserve: true, form }) ?? null;
  const city =
    Form.useWatch<string>("shippingCity", { preserve: true, form }) ?? null;
  const stateCode =
    Form.useWatch<string>("shippingState", { preserve: true, form }) ?? null;
  const zip =
    Form.useWatch<string>("shippingZip", { preserve: true, form }) ?? null;

  // ── Manual change detection — mirrors the wizard's useEffect exactly ─────

  useEffect(() => {
    // necessary as the watch values sometimes don't update all at once
    const values = form.getFieldsValue(true) as ShipToForm;

    if (currentlyAutofilledValue !== null) {
      const valueIsCustom =
        values.shippingName !== currentlyAutofilledValue.shippingName ||
        values.shippingAddress !== currentlyAutofilledValue.shippingAddress ||
        values.shippingCity !== currentlyAutofilledValue.shippingCity ||
        values.shippingZip !== currentlyAutofilledValue.shippingZip ||
        values.shippingState !== currentlyAutofilledValue.shippingState;

      if (valueIsCustom && selectedRadio !== AutofillRadio.manual) {
        setSelectedRadio(AutofillRadio.manual);
        setCurrentlyAutofilledValue(null);
      }
    }
  }, [name, address, city, stateCode, zip, currentlyAutofilledValue, selectedRadio]);

  // ── Autofill helpers ─────────────────────────────────────────────────────

  function autoFillPoolOwnerInfo() {
    const newValues = {
      shippingName: poolOwner.name ?? "",
      shippingAddress: poolOwner.address ?? "",
      shippingCity: poolOwner.city ?? "",
      shippingZip: poolOwner.zip ?? "",
      shippingState: poolOwner.state ?? "",
    };
    form.setFieldsValue(newValues);
    setSelectedRadio(AutofillRadio.poolOwner);
    setCurrentlyAutofilledValue(newValues);
  }

  async function autoFillBillToInfo() {
    const [response] = await getCustomerInfo(0);
    const info = response?.data?.data;
    if (
      !!info &&
      (!!info.contact ||
        !!info.address ||
        !!info.cityName ||
        !!info.zipCode ||
        !!info.stateId)
    ) {
      const autofilledValue = {
        shippingName: info.company,
        shippingAddress: !!info.address ? info.address.trim() : "",
        shippingCity: !!info.cityName ? info.cityName.trim() : "",
        shippingZip: !!info.zipCode ? info.zipCode.trim() : "",
        shippingState: !!info.stateId ? info.stateId.trim() : "",
      };
      form.setFieldsValue(autofilledValue);
      setSelectedRadio(AutofillRadio.billTo);
      setCurrentlyAutofilledValue(autofilledValue);
    }
  }

  function autoFill(option: AutofilledValue) {
    form.setFieldsValue({
      shippingName: !!option.shippingName ? option.shippingName : "",
      shippingAddress: !!option.shippingAddress ? option.shippingAddress : "",
      shippingCity: !!option.shippingCity ? option.shippingCity : "",
      shippingState: !!option.shippingState ? option.shippingState : "",
      shippingZip: !!option.shippingZip ? option.shippingZip : "",
    });
  }

  // ── Zip autofill — replicated inline from ZipCodeInputWithAutofill ───────
  // ZipCodeInputWithAutofill is typed to CoverWizardForm and cannot be reused
  // directly here.

  function handleZipChange(val: string | number | null) {
    const strVal = val != null ? String(val) : "";
    const patch: Partial<ShipToForm> = { shippingZip: strVal };

    if (strVal.length >= 5) {
      const lookup = zipCodes.lookup(strVal);
      if (lookup) {
        patch.shippingCity = lookup.city;
        patch.shippingState = lookup.state;
      }
    }

    form.setFieldsValue(patch);
  }

  // ── OK / Cancel ──────────────────────────────────────────────────────────

  async function handleOk() {
    setConfirmLoading(true);
    try {
      const values = await form.validateFields();
      onSave({
        shippingName: values.shippingName ?? "",
        shippingAddress: values.shippingAddress ?? "",
        shippingCity: values.shippingCity ?? "",
        shippingState: values.shippingState ?? "",
        shippingZip: values.shippingZip ?? "",
      });
      onClose();
    } catch {
      // validation failed — stay open so the user can fix errors
    } finally {
      setConfirmLoading(false);
    }
  }

  function handleCancel() {
    onClose();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Modal
      title="Edit Ship To Address"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Confirm"
      okButtonProps={{
        disabled: !name || !address || !city || !stateCode || !zip || addressesLoading,
      }}
      confirmLoading={confirmLoading}
      destroyOnClose={false}
      centered
      width={520}
      bodyStyle={{ overflowX: "hidden" }}
    >
        <Form
          form={form}
          layout="vertical"
          colon={false}
          preserve
          style={{ marginTop: 12 }}
        >
          <Flex vertical gap="1rem">
            <Radio.Group
              value={selectedRadio}
              onChange={(e) => setSelectedRadio(e.target.value)}
            >
              <Flex vertical gap="1rem">
                {/* Row 1: Pool Owner | Bill-To */}
                <Flex style={{ width: "100%" }} justify="space-between" align="center">
                  <Radio value={AutofillRadio.poolOwner} onClick={autoFillPoolOwnerInfo}>
                    Pool Owner Information
                  </Radio>
                  <Radio
                    value={AutofillRadio.billTo}
                    onClick={autoFillBillToInfo}
                    style={{ width: "165px" }}
                  >
                    Bill-To Information
                  </Radio>
                </Flex>
                {/* Row 2: Fill out manually | Ship-to existing address */}
                <Flex style={{ width: "100%" }} justify="space-between" align="center">
                  <Radio value={AutofillRadio.manual}>Fill out manually</Radio>
                  <ShipToAutofillRadio
                    setShipToInfo={autoFill}
                    setSelectedAutofillItem={setSelectedRadio}
                    autofillShipToInfo={autoFill}
                    customerAddresses={shipToAddresses}
                    setRadioValue={setSelectedRadio}
                    setCurrentlyAutofilledValue={setCurrentlyAutofilledValue}
                    addressesLoading={addressesLoading}
                    selectedShipToItemState={[selectedShipToItem, setSelectedShipToItem]}
                  />
                </Flex>
              </Flex>
            </Radio.Group>

            <Row gutter={[10, 10]}>
              <Col span={24}>
                <Form.Item style={{ margin: "0" }} name="shippingName" label="Name" required>
                  <Input placeholder="John Doe" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item style={{ margin: "0" }} name="shippingAddress" label="Address" required>
                  <Input placeholder="1600 Pennsylvania Avenue" />
                </Form.Item>
              </Col>
              <Col xs={12} md={5}>
                <div id="ShipToZip">
                  <FormItemLabel prefixCls="ant-form-item-label" label="Zip *" />
                  <Form.Item name="shippingZip" style={{ margin: "0" }} required>
                    <InputNumber
                      style={{ width: "100%", marginTop: "4px" }}
                      placeholder="#####"
                      onChange={handleZipChange}
                      maxLength={5}
                      stringMode
                    />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} md={14}>
                <Form.Item style={{ margin: "0" }} name="shippingCity" label="City" required>
                  <Input placeholder="Washington" />
                </Form.Item>
              </Col>
              <Col xs={12} md={5}>
                <Form.Item style={{ margin: "0" }} name="shippingState" label="State" required>
                  <Select placeholder="AZ">
                    {usStateOptions.map((s) => (
                      <Select.Option key={s.id} value={s.id}>
                        {s.id}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Flex>
        </Form>
    </Modal>
  );
};

export default ShipToAddressModal;
